import esCommon from './locales/es/common.json';
import { fetchCatalog } from './catalog-http';
import {
  DEFAULT_LOCALE, detectBrowserLocale, direction, normalizePreference,
  type LanguagePreference, type Locale,
} from './locales';

export type Params = Record<string, string | number>;
export type Message = string | Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };
export type Catalog = Record<string, Message>;
export interface LocaleSnapshot {
  locale: Locale;
  preference: LanguagePreference;
  requestedPreference: LanguagePreference;
  pending: boolean;
  error: boolean;
  revision: number;
}

// Only asset URLs are eager; catalog bodies stay in separate UTF-8 JSON files.
// no-inline also keeps small catalogs out of the JS bundle. Fetch can retry a
// failed download, whereas browsers cache a rejected dynamic import by URL.
// Spanish common is the sole synchronous fallback.
const urls = import.meta.glob<string>('./locales/*/*.json', {
  query: '?url&no-inline', import: 'default', eager: true,
});
const catalogs = new Map<string, Catalog>([['es/common', esCommon]]);
const loads = new Map<string, Promise<Catalog>>();
// Only mounted modules participate in a language switch. Previously visited
// modules remain cached, but do not download another language until reopened.
const namespaces = new Map<string, number>([['common', 1]]);
const listeners = new Set<() => void>();
let snapshot: LocaleSnapshot = {
  locale: DEFAULT_LOCALE, preference: 'auto', requestedPreference: 'auto', pending: false, error: false, revision: 0,
};
let request = 0;

function publish(patch: Partial<LocaleSnapshot>) {
  snapshot = { ...snapshot, ...patch, revision: snapshot.revision + 1 };
  listeners.forEach((listener) => listener());
}
function paint(locale: Locale) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
  document.documentElement.dir = direction(locale);
}

export async function loadCatalog(locale: Locale, namespace: string): Promise<Catalog> {
  const key = `${locale}/${namespace}`;
  const cached = catalogs.get(key);
  if (cached) return cached;
  const existing = loads.get(key);
  if (existing) return existing;
  const url = urls[`./locales/${key}.json`];
  if (!url) throw new Error(`Missing locale catalog: ${key}`);
  const promise = fetchCatalog(url).then((catalog) => {
    catalogs.set(key, catalog);
    return catalog;
  }).finally(() => loads.delete(key));
  loads.set(key, promise);
  return promise;
}

/** A failed download keeps the last complete locale and allows a retry. */
export async function setLanguagePreference(preference: LanguagePreference): Promise<boolean> {
  const normalized = normalizePreference(preference);
  const locale = normalized === 'auto' ? detectBrowserLocale() : normalized;
  const current = ++request;
  publish({ requestedPreference: normalized, pending: true, error: false });
  try {
    // A route may mount during a download. Include newly mounted modules before
    // committing, so their forms do not disappear when the locale changes.
    do {
      await Promise.all([...namespaces.keys()].flatMap((namespace) => [
        loadCatalog(DEFAULT_LOCALE, namespace), loadCatalog(locale, namespace),
      ]));
      if (current !== request) return false;
    } while ([...namespaces.keys()].some((namespace) => !isNamespaceReady(namespace, locale)));
    if (current !== request) return false;
    paint(locale);
    publish({ locale, preference: normalized, pending: false, error: false });
    return true;
  } catch {
    if (current === request) publish({ pending: false, error: true });
    return false;
  }
}

/** Called by a mounted feature hook; not by eager route or auth imports. */
export async function ensureNamespace(namespace: string, locale = snapshot.locale): Promise<void> {
  if (isNamespaceReady(namespace, locale)) return;
  await Promise.all([loadCatalog(DEFAULT_LOCALE, namespace), loadCatalog(locale, namespace)]);
  publish({});
}

export function isNamespaceReady(namespace: string, locale = snapshot.locale): boolean {
  return catalogs.has(`${DEFAULT_LOCALE}/${namespace}`) && catalogs.has(`${locale}/${namespace}`);
}

export function retainNamespace(namespace: string): () => void {
  namespaces.set(namespace, (namespaces.get(namespace) ?? 0) + 1);
  return () => {
    const count = (namespaces.get(namespace) ?? 1) - 1;
    if (count > 0) namespaces.set(namespace, count);
    else namespaces.delete(namespace);
  };
}

export const getLocaleSnapshot = () => snapshot;
export function subscribeLocale(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

const plurals = new Map<Locale, Intl.PluralRules>();
export function translate(key: string, params: Params = {}, namespace = 'common', locale = snapshot.locale): string {
  let message = catalogs.get(`${locale}/${namespace}`)?.[key] ??
    catalogs.get(`${DEFAULT_LOCALE}/${namespace}`)?.[key];
  if (message === undefined) return key;
  if (typeof message !== 'string') {
    const count = Number(params.count ?? 0);
    let rules = plurals.get(locale);
    if (!rules) { rules = new Intl.PluralRules(locale); plurals.set(locale, rules); }
    message = message[rules.select(count)] ?? message.other;
  }
  // React escapes this text. Do not pass catalog messages to innerHTML.
  return message.replace(/\{([a-zA-Z][a-zA-Z0-9_]*)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name)
      ? name === "count" && typeof params[name] === "number"
        ? formatNumber(params[name], { useGrouping: false }, locale) : String(params[name])
      : match);
}

type Formatter = Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat | Intl.Collator;
const formatters = new Map<string, Formatter>();
function formatter<T extends Formatter>(key: string, make: () => T): T {
  const cached = formatters.get(key);
  if (cached) return cached as T;
  if (formatters.size >= 64) formatters.delete(formatters.keys().next().value!);
  const value = make(); formatters.set(key, value); return value;
}
export function formatNumber(value: number, options: Intl.NumberFormatOptions = {}, locale = snapshot.locale) {
  return formatter(`number:${locale}:${JSON.stringify(options)}`, () => new Intl.NumberFormat(locale, options)).format(value);
}
export function compareText(a: string, b: string, options: Intl.CollatorOptions = { sensitivity:'base' }, locale = snapshot.locale): number {
  return formatter(`collator:${locale}:${JSON.stringify(options)}`, () => new Intl.Collator(locale,options)).compare(a,b);
}
export function formatDate(value: Date | string | number, options: Intl.DateTimeFormatOptions = {}, locale = snapshot.locale) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatter(`date:${locale}:${JSON.stringify(options)}`, () => new Intl.DateTimeFormat(locale, options)).format(date);
}

export function formatRelativeDate(value: string | null | undefined, locale = snapshot.locale, now = Date.now()) {
  if (!value) return '—';
  const instant = Date.parse(value);
  if (!Number.isFinite(instant)) return '—';
  const delta = (instant - now) / 1000, magnitude = Math.abs(delta);
  if (magnitude >= 30 * 86400) return formatDate(instant, { year: 'numeric', month: 'short', day: '2-digit' }, locale);
  const unit: Intl.RelativeTimeFormatUnit = magnitude < 60 ? 'second' : magnitude < 3600 ? 'minute' : magnitude < 86400 ? 'hour' : 'day';
  const divisor = unit === 'second' ? 1 : unit === 'minute' ? 60 : unit === 'hour' ? 3600 : 86400;
  return formatter(`relative:${locale}`, () => new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })).format(Math.round(delta / divisor), unit);
}
